import React, { useState, useEffect } from 'react';
import { Typography, Button, Input, Alert, IconButton, Spinner } from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useNavigate, NavLink } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { brandLogo as logo, brandName, isMaisterchef, mcDePageClass } from '../utils/branding';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';

const Signup = ({ onLogin }) => { // Accept onLogin prop
    const navigate = useNavigate();
    // Form state (for non-PT flow)
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [mobileNumber, setMobileNumber] = useState('');
    // General state
    const [sessionId, setSessionId] = useState(''); // Holds the 's' param
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertColor, setAlertColor] = useState('red');
    const hasCheckedSession = React.useRef(false); // Prevent double checking
    const { language, setLanguage } = useLanguage();

    // Map befr to french and benl to nederlands for translations
    let translationLanguage = language;
    if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
    if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
    if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
    if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
    if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
    if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
    if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
    if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
    if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
    if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
    if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
    if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
    if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
    if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
    if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
    if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
    if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
    if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
    if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
    if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
    if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
    if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
    if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
    if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
    if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
    if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
    if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
    if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
    if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

    const t = languages[translationLanguage] || languages['english']; // Fallback to English if translation not found
    // PT Flow specific state
    const [isPortugueseFlow, setIsPortugueseFlow] = useState(false);
    const [msisdn, setMsisdn] = useState('');
    const [ptSignupStatus, setPtSignupStatus] = useState('idle'); // idle, validating, processing_signup, success, error
    const [generatedPassword, setGeneratedPassword] = useState('********');
    // Arabic Flow specific state
    const [isArabicFlow, setIsArabicFlow] = useState(false);
    const [arabicSignupStatus, setArabicSignupStatus] = useState('idle'); // idle, validating, processing_signup, success, error
    // German Flow specific state
    const [isGermanFlow, setIsGermanFlow] = useState(false);
    const [germanSignupStatus, setGermanSignupStatus] = useState('idle'); // idle, validating, processing_signup, success, error

    // Password requirements state (only for non-PT flow)
    const [passwordRequirements, setPasswordRequirements] = useState({
        minLength: false, hasUpperCase: false, hasLowerCase: false, hasNumber: false, hasSpecial: false, matching: false
    });

    // --- Auto Signup Logic for PT Flow ---

    // Check if mobile number has valid subscription and redirect with sessionId
    const checkMobileNumberAndRedirect = async (mobileNumber) => {
        try {
            console.log('Checking subscription for mobile number:', mobileNumber);
            const response = await fetch('/api/login/phone', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phoneNumber: mobileNumber
                }),
            });

            const data = await response.json();
            console.log('Mobile number check response:', data);

            if (response.ok && data.isSubscribed && data.accountSessionId) {
                console.log('Mobile number has valid subscription, redirecting with sessionId');
                // Redirect to signup with both msisdn and sessionId
                navigate(`/signup?msisdn=${mobileNumber}&s=${data.accountSessionId}`);
            } else {
                // No valid subscription, just pre-fill the mobile number
                console.log('Mobile number does not have valid subscription, pre-filling number only');
                setMobileNumber(mobileNumber);
            }
        } catch (error) {
            console.error('Error checking mobile number subscription:', error);
            // On error, just pre-fill the mobile number
            setMobileNumber(mobileNumber);
        }
    };

    // Check mobile number for Portugal flow (works with just msisdn)
    const checkMobileNumberAndRedirectForPT = async (mobileNumber) => {
        try {
            console.log('Portugal flow: Checking subscription for mobile number:', mobileNumber);
            const response = await fetch('/api/login/phone', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phoneNumber: mobileNumber
                }),
            });

            const data = await response.json();
            console.log('Mobile number check response:', data);

            if (response.ok && data.isSubscribed && data.accountSessionId) {
                console.log('Mobile number has valid subscription, proceeding with PT auto-signup');
                // Use the sessionId from the phone check for PT signup
                await performPtAutoSignup(mobileNumber, data.accountSessionId, { redirectToSignup: true });
            } else {
                // No valid subscription - show error for PT flow
                console.log('Mobile number does not have valid subscription for PT flow');
                setAlertMessage('Erro: Número de telemóvel sem subscrição válida.');
                setAlertColor('red');
                setShowAlert(true);
                setPtSignupStatus('error');
            }
        } catch (error) {
            console.error('Error checking mobile number subscription for PT:', error);
            setAlertMessage('Erro ao verificar a subscrição.');
            setAlertColor('red');
            setShowAlert(true);
            setPtSignupStatus('error');
        }
    };

    // New function to check if session is already subscribed and redirect to login
    const checkSessionAndRedirect = async (sessionIdFromUrl) => {
        try {
            const validationUrl = `/api/login/check-session?s=${sessionIdFromUrl}`;
            console.log('Checking if session is already subscribed at:', validationUrl);
            const validationResponse = await axios.get(validationUrl);
            const sessionValidationData = validationResponse.data;
            console.log('Session check response:', sessionValidationData);

            if (sessionValidationData?.isValid && sessionValidationData?.username) {
                console.log('Session is already subscribed and user exists, redirecting to login');
                // Store the session data for login
                localStorage.setItem('username', sessionValidationData.username);
                if (sessionValidationData.userId) {
                    localStorage.setItem('userId', sessionValidationData.userId);
                }
                localStorage.setItem('sessionId', sessionIdFromUrl);
                localStorage.setItem('userData', JSON.stringify(sessionValidationData));
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 }); // 1 day
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    Cookies.set('isLoggedIn', 'true');
                }

                // Redirect to login page with a message
                setAlertMessage(t.alreadySubscribed || 'You are already registered. Redirecting to login.');
                setAlertColor('green');
                setShowAlert(true);

                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 2000);

                return true; // Indicates we should not proceed with signup
            } else if (sessionValidationData?.redirectToSignup) {
                // Session is valid but user doesn't exist yet - show message to complete signup
                console.log('Session is valid but user not found, showing signup form');
                setAlertMessage(t.sessionValidCompleteSignup || 'Your subscription is active. Please complete your account registration below.');
                setAlertColor('green');
                setShowAlert(true);
                setTimeout(() => setShowAlert(false), 5000);
                return false; // Continue with signup flow
            }
        } catch (error) {
            console.error('Error checking session for redirect:', error);
            // Continue with normal signup flow if check fails
        }
        return false; // Continue with normal signup flow
    };

    const performPtAutoSignup = async (msisdnToSignUp, currentSessionId, sessionValidationData) => {
        setPtSignupStatus('processing_signup');
        const payload = {
            isPortugueseFlow: true,
            msisdn: msisdnToSignUp,
            email: '', // Send empty email for PT flow
            sessionId: currentSessionId
        };

        try {
            console.log('Attempting PT auto-signup with payload:', payload);
            const response = await fetch('/api/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const signupResponseData = await response.json();

            if (response.ok) {
                console.log('PT auto-signup successful:', signupResponseData);
                // Handle both new signup and "already exists" cases
                if (signupResponseData.alreadyExists) {
                    console.log('User already exists, proceeding with login using returned user data.');
                }
                localStorage.setItem('username', signupResponseData.username || msisdnToSignUp);
                if (signupResponseData.userId) {
                    localStorage.setItem('userId', signupResponseData.userId);
                }
                localStorage.setItem('sessionId', currentSessionId); // Store the 's' parameter
                // Safely store user data
                const safeUserData = {
                    userId: signupResponseData.userId,
                    username: signupResponseData.username || msisdnToSignUp,
                    alreadyExists: signupResponseData.alreadyExists || false
                };
                localStorage.setItem('userData', JSON.stringify(safeUserData));
                // Set cookie with simple options to avoid cookie library errors
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Continue anyway, cookie is not critical
                }
                setPtSignupStatus('success');
            } else {
                console.error('PT auto-signup failed:', signupResponseData);
                // Check for both "already exists" and "Duplicate entry" error messages (fallback for non-PT flow errors)
                if ((response.status === 400 || response.status === 500) &&
                    (signupResponseData.error?.includes('already exists') || signupResponseData.details?.includes('Duplicate entry'))) {
                    console.log('User likely already exists, treating as success for login. Fetching user data from check-session.');
                    localStorage.setItem('username', msisdnToSignUp); // Default to msisdn
                    localStorage.setItem('sessionId', currentSessionId); // Store the 's' parameter

                    // Try to get user data from check-session endpoint
                    try {
                        const checkSessionUrl = `/api/login/check-session?s=${currentSessionId}`;
                        const checkSessionResponse = await fetch(checkSessionUrl);
                        const checkSessionData = await checkSessionResponse.json();

                        if (checkSessionData?.isValid && checkSessionData?.userId) {
                            // Use data from check-session
                            localStorage.setItem('userId', checkSessionData.userId);
                            if (checkSessionData.username) {
                                localStorage.setItem('username', checkSessionData.username);
                            }
                            // Safely stringify the data, ensuring all values are serializable
                            const safeUserData = {
                                userId: checkSessionData.userId,
                                username: checkSessionData.username || msisdnToSignUp,
                                accountSessionId: checkSessionData.accountSessionId,
                                paymentMethod: checkSessionData.paymentMethod,
                                subscriptionDetails: checkSessionData.subscriptionDetails
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        } else if (sessionValidationData && sessionValidationData.userId && sessionValidationData.isValid) {
                            // Fallback to sessionValidationData if check-session didn't work
                            localStorage.setItem('userId', sessionValidationData.userId);
                            if (sessionValidationData.username) {
                                localStorage.setItem('username', sessionValidationData.username);
                            }
                            // Safely stringify the data
                            const safeUserData = {
                                userId: sessionValidationData.userId,
                                username: sessionValidationData.username || msisdnToSignUp,
                                accountSessionId: sessionValidationData.accountSessionId,
                                paymentMethod: sessionValidationData.paymentMethod,
                                subscriptionDetails: sessionValidationData.subscriptionDetails
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        } else {
                            // Last resort: just store basic info
                            console.warn('Could not retrieve full user data for "already exists" user. Storing basic info only.');
                            localStorage.removeItem('userData');
                        }
                    } catch (fetchError) {
                        console.error('Error fetching user data from check-session:', fetchError);
                        // If fetch fails, try to use sessionValidationData if available
                        if (sessionValidationData && sessionValidationData.userId) {
                            localStorage.setItem('userId', sessionValidationData.userId);
                            if (sessionValidationData.username) {
                                localStorage.setItem('username', sessionValidationData.username);
                            }
                            // Safely stringify only the essential data
                            const safeUserData = {
                                userId: sessionValidationData.userId,
                                username: sessionValidationData.username || msisdnToSignUp
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        }
                    }
                    // Set cookie with simple options to avoid cookie library errors
                    try {
                        Cookies.set('isLoggedIn', 'true', { expires: 1 });
                    } catch (cookieError) {
                        console.error('Error setting cookie:', cookieError);
                        // Continue anyway, cookie is not critical
                    }
                    setPtSignupStatus('success');
                } else {
                    setAlertMessage(signupResponseData.error || t.signupError || 'Erro no registo automático.');
                    setAlertColor('red');
                    setShowAlert(true);
                    setPtSignupStatus('error');
                }
            }
        } catch (error) {
            console.error('PT auto-signup network/fetch error:', error);
            setAlertMessage(t.signupError || 'Erro de rede durante o registo.');
            setAlertColor('red');
            setShowAlert(true);
            setPtSignupStatus('error');
        }
    };

    const performArabicAutoSignup = async (msisdnToSignUp, currentSessionId, sessionValidationData) => {
        // Validate that session ID is present before proceeding
        if (!currentSessionId) {
            console.error('Arabic auto-signup called without session ID');
            setAlertMessage('خطأ: معرف الجلسة مطلوب للتسجيل.');
            setAlertColor('red');
            setShowAlert(true);
            setArabicSignupStatus('error');
            return;
        }

        setArabicSignupStatus('processing_signup');
        // For Arabic flow, msisdn is optional - backend will extract it from session if not provided
        const payload = {
            isArabicFlow: true,
            msisdn: msisdnToSignUp || null, // Can be null, backend will extract from session
            email: '', // Send empty email for Arabic flow
            sessionId: currentSessionId
        };

        try {
            console.log('Attempting Arabic auto-signup with payload:', payload);
            const response = await fetch('/api/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const signupResponseData = await response.json();

            if (response.ok) {
                console.log('Arabic auto-signup successful:', signupResponseData);
                // Handle both new signup and "already exists" cases
                if (signupResponseData.alreadyExists) {
                    console.log('User already exists, proceeding with login using returned user data.');
                }
                // Use username from response, or msisdn from signup, or from session validation data
                const finalUsername = signupResponseData.username || msisdnToSignUp || sessionValidationData?.username || sessionValidationData?.msisdn;
                if (finalUsername) {
                    localStorage.setItem('username', finalUsername);
                    setMsisdn(finalUsername); // Update state for display
                }
                if (signupResponseData.userId) {
                    localStorage.setItem('userId', signupResponseData.userId);
                }
                localStorage.setItem('sessionId', currentSessionId); // Store the 's' parameter
                // Safely store user data
                const safeUserData = {
                    userId: signupResponseData.userId,
                    username: finalUsername,
                    alreadyExists: signupResponseData.alreadyExists || false
                };
                localStorage.setItem('userData', JSON.stringify(safeUserData));
                // Set cookie with simple options to avoid cookie library errors
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Continue anyway, cookie is not critical
                }
                setArabicSignupStatus('success');
            } else {
                console.error('Arabic auto-signup failed:', signupResponseData);
                // Check for both "already exists" and "Duplicate entry" error messages (fallback for non-Arabic flow errors)
                if ((response.status === 400 || response.status === 500) &&
                    (signupResponseData.error?.includes('already exists') || signupResponseData.details?.includes('Duplicate entry'))) {
                    console.log('User likely already exists, treating as success for login. Fetching user data from check-session.');
                    localStorage.setItem('sessionId', currentSessionId); // Store the 's' parameter

                    // Try to get user data from check-session endpoint
                    try {
                        const checkSessionUrl = `/api/login/check-session?s=${currentSessionId}`;
                        const checkSessionResponse = await fetch(checkSessionUrl);
                        const checkSessionData = await checkSessionResponse.json();

                        if (checkSessionData?.isValid && checkSessionData?.userId) {
                            // Use data from check-session
                            localStorage.setItem('userId', checkSessionData.userId);
                            const finalUsername = checkSessionData.username || msisdnToSignUp || sessionValidationData?.username || sessionValidationData?.msisdn;
                            if (finalUsername) {
                                localStorage.setItem('username', finalUsername);
                                setMsisdn(finalUsername); // Update state for display
                            }
                            // Safely stringify the data, ensuring all values are serializable
                            const safeUserData = {
                                userId: checkSessionData.userId,
                                username: finalUsername,
                                accountSessionId: checkSessionData.accountSessionId,
                                paymentMethod: checkSessionData.paymentMethod,
                                subscriptionDetails: checkSessionData.subscriptionDetails
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        } else if (sessionValidationData && sessionValidationData.userId && sessionValidationData.isValid) {
                            // Fallback to sessionValidationData if check-session didn't work
                            localStorage.setItem('userId', sessionValidationData.userId);
                            const finalUsername = sessionValidationData.username || msisdnToSignUp || sessionValidationData?.msisdn;
                            if (finalUsername) {
                                localStorage.setItem('username', finalUsername);
                                setMsisdn(finalUsername); // Update state for display
                            }
                            // Safely stringify the data
                            const safeUserData = {
                                userId: sessionValidationData.userId,
                                username: finalUsername,
                                accountSessionId: sessionValidationData.accountSessionId,
                                paymentMethod: sessionValidationData.paymentMethod,
                                subscriptionDetails: sessionValidationData.subscriptionDetails
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        } else {
                            // Last resort: just store basic info
                            console.warn('Could not retrieve full user data for "already exists" user. Storing basic info only.');
                            localStorage.removeItem('userData');
                        }
                    } catch (fetchError) {
                        console.error('Error fetching user data from check-session:', fetchError);
                        // If fetch fails, try to use sessionValidationData if available
                        if (sessionValidationData && sessionValidationData.userId) {
                            localStorage.setItem('userId', sessionValidationData.userId);
                            const finalUsername = sessionValidationData.username || msisdnToSignUp || sessionValidationData?.msisdn;
                            if (finalUsername) {
                                localStorage.setItem('username', finalUsername);
                                setMsisdn(finalUsername); // Update state for display
                            }
                            // Safely stringify only the essential data
                            const safeUserData = {
                                userId: sessionValidationData.userId,
                                username: finalUsername
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        }
                    }
                    // Set cookie with simple options to avoid cookie library errors
                    try {
                        Cookies.set('isLoggedIn', 'true', { expires: 1 });
                    } catch (cookieError) {
                        console.error('Error setting cookie:', cookieError);
                        // Continue anyway, cookie is not critical
                    }
                    setArabicSignupStatus('success');
                } else {
                    setAlertMessage(signupResponseData.error || t.signupError || 'خطأ في التسجيل التلقائي.');
                    setAlertColor('red');
                    setShowAlert(true);
                    setArabicSignupStatus('error');
                }
            }
        } catch (error) {
            console.error('Arabic auto-signup network/fetch error:', error);
            setAlertMessage(t.signupError || 'خطأ في الشبكة أثناء التسجيل.');
            setAlertColor('red');
            setShowAlert(true);
            setArabicSignupStatus('error');
        }
    };

    // Updated function to validate session using /api/login/check-session
    const validateSessionAndAutoSignup = async (msisdnFromUrl, sessionIdFromUrl) => {
        setPtSignupStatus('validating');
        try {
            const validationUrl = `/api/login/check-session?s=${sessionIdFromUrl}`;
            console.log('Validating session at:', validationUrl);
            const validationResponse = await axios.get(validationUrl); // Renamed to validationResponse
            const sessionValidationData = validationResponse.data; // Extracted data
            console.log('Session validation response:', sessionValidationData);

            if (sessionValidationData?.isValid) {
                console.log('Session validation successful (isValid:true). User confirmed/created by check-session. Storing data.');
                localStorage.setItem('username', sessionValidationData.username || msisdnFromUrl);
                if (sessionValidationData.userId) {
                    localStorage.setItem('userId', sessionValidationData.userId);
                }
                localStorage.setItem('sessionId', sessionIdFromUrl); // Store the 's' parameter from URL
                localStorage.setItem('userData', JSON.stringify(sessionValidationData)); // Store all data from check-session
                // Set cookie with simple options to avoid cookie library errors
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Continue anyway, cookie is not critical
                }
                setPtSignupStatus('success');
            } else if (sessionValidationData?.redirectToSignup) {
                console.log('Session validation indicates redirectToSignup. Proceeding to full signup attempt via /api/signup.');
                // Pass sessionData (which might contain partial info like accountSessionId from MSP)
                await performPtAutoSignup(msisdnFromUrl, sessionIdFromUrl, sessionValidationData);
            } else {
                console.log('Session validation failed or subscription inactive:', sessionValidationData?.error || 'Unknown validation issue');
                setAlertMessage(sessionValidationData?.error || 'Sessão inválida ou subscrição inativa.');
                setAlertColor('red');
                setShowAlert(true);
                setPtSignupStatus('error');
            }
        } catch (error) {
            console.error('Error validating session:', error);
            let specificError = 'Erro ao validar a sessão.';
            if (axios.isAxiosError(error) && error.response) {
                specificError = `Erro ${error.response.status}: ${error.response.data?.error || 'Falha na validação'}`;
            }
            setAlertMessage(specificError);
            setAlertColor('red');
            setShowAlert(true);
            setPtSignupStatus('error');
        }
    };

    // German Flow auto-signup functions (similar to Arabic flow - works with session only)
    const performGermanAutoSignup = async (msisdnToSignUp, currentSessionId, sessionValidationData) => {
        // Validate that session ID is present before proceeding
        if (!currentSessionId) {
            console.error('German auto-signup called without session ID');
            setAlertMessage('Fehler: Sitzungs-ID ist für die Registrierung erforderlich.');
            setAlertColor('red');
            setShowAlert(true);
            setGermanSignupStatus('error');
            return;
        }

        setGermanSignupStatus('processing_signup');
        // For German flow, msisdn is optional - backend will extract it from session if not provided
        const payload = {
            isGermanFlow: true,
            msisdn: msisdnToSignUp || null, // Can be null, backend will extract from session
            email: '', // Send empty email for German flow
            sessionId: currentSessionId
        };

        try {
            console.log('Attempting German auto-signup with payload:', payload);
            const response = await fetch('/api/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const signupResponseData = await response.json();

            if (response.ok) {
                console.log('German auto-signup successful:', signupResponseData);
                // Handle both new signup and "already exists" cases
                if (signupResponseData.alreadyExists) {
                    console.log('User already exists, proceeding with login using returned user data.');
                }
                // For German flow: use userName and password from response
                const usernameToUse = signupResponseData.userName || signupResponseData.username || msisdnToSignUp || sessionValidationData?.username || sessionValidationData?.msisdn;
                const passwordToUse = signupResponseData.password || generatedPassword;
                
                if (usernameToUse) {
                    localStorage.setItem('username', usernameToUse);
                    setMsisdn(usernameToUse); // Update state for display
                }
                if (passwordToUse) {
                    setGeneratedPassword(passwordToUse); // Update password for display
                }
                if (signupResponseData.userId) {
                    localStorage.setItem('userId', signupResponseData.userId);
                }
                localStorage.setItem('sessionId', currentSessionId); // Store the 's' parameter
                // Safely store user data
                const safeUserData = {
                    userId: signupResponseData.userId,
                    username: usernameToUse,
                    alreadyExists: signupResponseData.alreadyExists || false
                };
                localStorage.setItem('userData', JSON.stringify(safeUserData));
                // Set cookie with simple options to avoid cookie library errors
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Continue anyway, cookie is not critical
                }
                setGermanSignupStatus('success');
            } else {
                console.error('German auto-signup failed:', signupResponseData);
                // Check for both "already exists" and "Duplicate entry" error messages (fallback for non-German flow errors)
                if ((response.status === 400 || response.status === 500) &&
                    (signupResponseData.error?.includes('already exists') || signupResponseData.details?.includes('Duplicate entry'))) {
                    console.log('User likely already exists, treating as success for login. Fetching user data from check-session.');
                    localStorage.setItem('sessionId', currentSessionId); // Store the 's' parameter

                    // Try to get user data from check-session endpoint
                    try {
                        const checkSessionUrl = `/api/login/check-session?s=${currentSessionId}`;
                        const checkSessionResponse = await fetch(checkSessionUrl);
                        const checkSessionData = await checkSessionResponse.json();

                        if (checkSessionData?.isValid && checkSessionData?.userId) {
                            // Use data from check-session - for German flow: use userName and password from response
                            localStorage.setItem('userId', checkSessionData.userId);
                            const finalUsername = checkSessionData.userName || checkSessionData.username || msisdnToSignUp || sessionValidationData?.username || sessionValidationData?.msisdn;
                            const finalPassword = checkSessionData.password || generatedPassword;
                            if (finalUsername) {
                                localStorage.setItem('username', finalUsername);
                                setMsisdn(finalUsername); // Update state for display
                            }
                            if (finalPassword) {
                                setGeneratedPassword(finalPassword); // Update password for display
                            }
                            // Safely stringify the data, ensuring all values are serializable
                            const safeUserData = {
                                userId: checkSessionData.userId,
                                username: finalUsername,
                                accountSessionId: checkSessionData.accountSessionId,
                                paymentMethod: checkSessionData.paymentMethod,
                                subscriptionDetails: checkSessionData.subscriptionDetails
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        } else if (sessionValidationData && sessionValidationData.userId && sessionValidationData.isValid) {
                            // Fallback to sessionValidationData if check-session didn't work - for German flow: use userName and password
                            localStorage.setItem('userId', sessionValidationData.userId);
                            const finalUsername = sessionValidationData.userName || sessionValidationData.username || msisdnToSignUp || sessionValidationData?.msisdn;
                            const finalPassword = sessionValidationData.password || generatedPassword;
                            if (finalUsername) {
                                localStorage.setItem('username', finalUsername);
                                setMsisdn(finalUsername); // Update state for display
                            }
                            if (finalPassword) {
                                setGeneratedPassword(finalPassword); // Update password for display
                            }
                            // Safely stringify the data
                            const safeUserData = {
                                userId: sessionValidationData.userId,
                                username: finalUsername,
                                accountSessionId: sessionValidationData.accountSessionId,
                                paymentMethod: sessionValidationData.paymentMethod,
                                subscriptionDetails: sessionValidationData.subscriptionDetails
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        } else {
                            // Last resort: just store basic info
                            console.warn('Could not retrieve full user data for "already exists" user. Storing basic info only.');
                            localStorage.removeItem('userData');
                        }
                    } catch (fetchError) {
                        console.error('Error fetching user data from check-session:', fetchError);
                        // If fetch fails, try to use sessionValidationData if available - for German flow: use userName and password
                        if (sessionValidationData && sessionValidationData.userId) {
                            localStorage.setItem('userId', sessionValidationData.userId);
                            const finalUsername = sessionValidationData.userName || sessionValidationData.username || msisdnToSignUp || sessionValidationData?.msisdn;
                            const finalPassword = sessionValidationData.password || generatedPassword;
                            if (finalUsername) {
                                localStorage.setItem('username', finalUsername);
                                setMsisdn(finalUsername); // Update state for display
                            }
                            if (finalPassword) {
                                setGeneratedPassword(finalPassword); // Update password for display
                            }
                            // Safely stringify only the essential data
                            const safeUserData = {
                                userId: sessionValidationData.userId,
                                username: finalUsername
                            };
                            localStorage.setItem('userData', JSON.stringify(safeUserData));
                        }
                    }
                    // Set cookie with simple options to avoid cookie library errors
                    try {
                        Cookies.set('isLoggedIn', 'true', { expires: 1 });
                    } catch (cookieError) {
                        console.error('Error setting cookie:', cookieError);
                        // Continue anyway, cookie is not critical
                    }
                    setGermanSignupStatus('success');
                } else {
                    setAlertMessage(signupResponseData.error || t.signupError || 'Fehler bei der automatischen Registrierung.');
                    setAlertColor('red');
                    setShowAlert(true);
                    setGermanSignupStatus('error');
                }
            }
        } catch (error) {
            console.error('German auto-signup network/fetch error:', error);
            setAlertMessage(t.signupError || 'Netzwerkfehler während der Registrierung.');
            setAlertColor('red');
            setShowAlert(true);
            setGermanSignupStatus('error');
        }
    };

    // Function to validate session and auto-signup for German flow (similar to PT, but can extract msisdn from session like Arabic)
    const validateSessionAndAutoSignupGerman = async (msisdnFromUrl, sessionIdFromUrl) => {
        setGermanSignupStatus('validating');
        try {
            const validationUrl = `/api/login/check-session?s=${sessionIdFromUrl}`;
            console.log('Validating session for German flow at:', validationUrl);
            const validationResponse = await axios.get(validationUrl);
            const sessionValidationData = validationResponse.data;
            console.log('Session validation response:', sessionValidationData);

            // Extract msisdn from session data if not provided
            const msisdnToUse = msisdnFromUrl || sessionValidationData?.msisdn || sessionValidationData?.phonenumber || sessionValidationData?.username;
            if (msisdnToUse) {
                setMsisdn(msisdnToUse);
            }

            if (sessionValidationData?.isValid) {
                console.log('Session validation successful (isValid:true). User confirmed/created by check-session. Storing data.');
                // For German flow: use userName and password from response
                const usernameToUse = sessionValidationData.userName || sessionValidationData.username || msisdnToUse;
                const passwordToUse = sessionValidationData.password || generatedPassword;
                
                localStorage.setItem('username', usernameToUse);
                if (usernameToUse) {
                    setMsisdn(usernameToUse); // Update state for display
                }
                if (passwordToUse) {
                    setGeneratedPassword(passwordToUse); // Update password for display
                }
                if (sessionValidationData.userId) {
                    localStorage.setItem('userId', sessionValidationData.userId);
                }
                localStorage.setItem('sessionId', sessionIdFromUrl); // Store the 's' parameter from URL
                localStorage.setItem('userData', JSON.stringify(sessionValidationData)); // Store all data from check-session
                // Set cookie with simple options to avoid cookie library errors
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Continue anyway, cookie is not critical
                }
                setGermanSignupStatus('success');
            } else if (sessionValidationData?.redirectToSignup) {
                console.log('Session validation indicates redirectToSignup. Proceeding to full signup attempt via /api/signup.');
                // Pass msisdn from session data or use the one from URL
                await performGermanAutoSignup(msisdnToUse, sessionIdFromUrl, sessionValidationData);
            } else {
                console.log('Session validation failed or subscription inactive:', sessionValidationData?.error || 'Unknown validation issue');
                setAlertMessage(sessionValidationData?.error || 'Ungültige Sitzung oder inaktives Abonnement.');
                setAlertColor('red');
                setShowAlert(true);
                setGermanSignupStatus('error');
            }
        } catch (error) {
            console.error('Error validating session:', error);
            let specificError = 'Fehler beim Validieren der Sitzung.';
            if (axios.isAxiosError(error) && error.response) {
                specificError = `Fehler ${error.response.status}: ${error.response.data?.error || 'Validierungsfehler'}`;
            }
            setAlertMessage(specificError);
            setAlertColor('red');
            setShowAlert(true);
            setGermanSignupStatus('error');
        }
    };

    // Function to validate session and auto-signup for Arabic flow (works with session only)
    const validateSessionAndAutoSignupArabic = async (msisdnFromUrl, sessionIdFromUrl) => {
        // Validate that session ID is present before proceeding
        if (!sessionIdFromUrl) {
            console.error('Arabic flow: Session ID is missing in validateSessionAndAutoSignupArabic');
            setAlertMessage('خطأ: معرف الجلسة مطلوب للتحقق.');
            setAlertColor('red');
            setShowAlert(true);
            setArabicSignupStatus('error');
            return;
        }

        setArabicSignupStatus('validating');
        try {
            const validationUrl = `/api/login/check-session?s=${sessionIdFromUrl}`;
            console.log('Validating session for Arabic flow at:', validationUrl);
            const validationResponse = await axios.get(validationUrl);
            const sessionValidationData = validationResponse.data;
            console.log('Session validation response:', sessionValidationData);

            // Extract msisdn from session data if not provided
            const msisdnToUse = msisdnFromUrl || sessionValidationData?.msisdn || sessionValidationData?.phonenumber || sessionValidationData?.username;
            if (msisdnToUse) {
                setMsisdn(msisdnToUse);
            }

            if (sessionValidationData?.isValid) {
                console.log('Session validation successful (isValid:true). User confirmed/created by check-session. Storing data.');
                localStorage.setItem('username', sessionValidationData.username || msisdnToUse);
                if (sessionValidationData.userId) {
                    localStorage.setItem('userId', sessionValidationData.userId);
                }
                localStorage.setItem('sessionId', sessionIdFromUrl); // Store the 's' parameter from URL
                localStorage.setItem('userData', JSON.stringify(sessionValidationData)); // Store all data from check-session
                // Set cookie with simple options to avoid cookie library errors
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    // Continue anyway, cookie is not critical
                }
                setArabicSignupStatus('success');
            } else if (sessionValidationData?.redirectToSignup) {
                console.log('Session validation indicates redirectToSignup. Proceeding to full signup attempt via /api/signup.');
                // Pass msisdn from session data or use null (backend will extract from session)
                await performArabicAutoSignup(msisdnToUse, sessionIdFromUrl, sessionValidationData);
            } else {
                console.log('Session validation failed or subscription inactive:', sessionValidationData?.error || 'Unknown validation issue');
                setAlertMessage(sessionValidationData?.error || 'الجلسة غير صالحة أو الاشتراك غير نشط.');
                setAlertColor('red');
                setShowAlert(true);
                setArabicSignupStatus('error');
            }
        } catch (error) {
            console.error('Error validating session:', error);
            let specificError = 'خطأ في التحقق من الجلسة.';
            if (axios.isAxiosError(error) && error.response) {
                specificError = `خطأ ${error.response.status}: ${error.response.data?.error || 'فشل التحقق'}`;
            }
            setAlertMessage(specificError);
            setAlertColor('red');
            setShowAlert(true);
            setArabicSignupStatus('error');
        }
    };

    // --- Effects ---

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const session = params.get('s');
        const urlLang = params.get('lang');
        const urlMsisdn = params.get('msisdn') || params.get('m'); // Support both msisdn and m parameters

        if (session) {
            setSessionId(session);
        } else {
            console.log('No session ID (s parameter) found in URL.');
        }

        // Check for Arabic flow (UAE) first - this should always trigger automatic signup
        // Support lang=ae, lang=ae-ar, lang=ae-en, lang=uae-ar, lang=uae-en
        const isUAEUrl = urlLang === 'ae' || urlLang === 'ae-ar' || urlLang === 'ae-en' || 
                         urlLang === 'uae-ar' || urlLang === 'uae-en' || urlLang === 'uae-arabic' || urlLang === 'uae-english';
        if (isUAEUrl) {
            if (session) {
                // Arabic flow: works ONLY with session (s parameter), no msisdn needed
                setIsArabicFlow(true);
                setIsPortugueseFlow(false);
                console.log('Arabic flow (UAE) detected. Session:', session, 'URL Lang:', urlLang, 'MSISDN:', urlMsisdn || 'will be retrieved from session');

                // Arabic flow works with session only - msisdn will be retrieved from session data
                validateSessionAndAutoSignupArabic(null, session); // Pass null for msisdn, it will be retrieved from session
            } else {
                // UAE flow requires session - show error
                console.error('Arabic flow (UAE) requires session ID (s parameter)');
                setAlertMessage('Session ID is required for UAE signup. Please use the signup link provided.');
                setAlertColor('red');
                setShowAlert(true);
                setIsArabicFlow(true);
                setArabicSignupStatus('error');
            }
        } else if (urlLang === 'pt' && urlMsisdn) {
            // Portugal flow: works with msisdn (with or without session)
            setIsPortugueseFlow(true);
            setIsArabicFlow(false);
            setIsGermanFlow(false);
            setMsisdn(urlMsisdn);
            console.log('Portuguese flow detected. MSISDN:', urlMsisdn, 'Session:', session || 'none');

            if (urlMsisdn === '11111111') {
                console.log('Fake Portuguese flow detected (11111111). Skipping validation and showing confirmation.');
                setPtSignupStatus('success'); // Show confirmation screen for testing
            } else if (session) {
                // If session is provided, use it
                validateSessionAndAutoSignup(urlMsisdn, session);
            } else {
                // Portugal can work with just msisdn - check subscription and get session
                console.log('Portuguese flow: No session provided, checking mobile number subscription...');
                checkMobileNumberAndRedirectForPT(urlMsisdn);
            }
        } else if (urlLang === 'de') {
            // German flow: works with session (requires session)
            setIsGermanFlow(true);
            setIsPortugueseFlow(false);
            setIsArabicFlow(false);
            
            // Set language to German
            if (setLanguage) {
                setLanguage('deutsch');
            }

            if (session) {
                // German flow: works ONLY with session (s parameter), no msisdn needed
                console.log('German flow detected. Session:', session, 'URL Lang:', urlLang, 'MSISDN:', urlMsisdn || 'will be retrieved from session');

                // German flow works with session only - msisdn will be retrieved from session data
                validateSessionAndAutoSignupGerman(null, session); // Pass null for msisdn, it will be retrieved from session
            } else {
                // German flow requires session - show error
                console.error('German flow requires session ID (s parameter)');
                setAlertMessage('Session-ID ist für die deutsche Anmeldung erforderlich. Bitte verwenden Sie den bereitgestellten Anmeldelink.');
                setAlertColor('red');
                setShowAlert(true);
                setIsGermanFlow(true);
                setGermanSignupStatus('error');
            }
        } else {
            setIsPortugueseFlow(false);
            setIsArabicFlow(false);
            setIsGermanFlow(false);
            setPtSignupStatus('idle');
            setArabicSignupStatus('idle');
            setGermanSignupStatus('idle');

            // If m= parameter exists without s=, check if mobile number has valid session
            if (urlMsisdn && !session && urlLang !== 'pt' && urlLang !== 'ae' && urlLang !== 'de') {
                console.log('Mobile number (m=) found without session, checking for valid subscription');
                checkMobileNumberAndRedirect(urlMsisdn);
            }

            // Always pre-fill mobile number if msisdn or m is in URL (for non-PT, non-Arabic, and non-German flow)
            // This ensures it's prefilled even after redirect with msisdn= and s= parameters
            if (urlMsisdn && urlLang !== 'pt' && urlLang !== 'ae' && urlLang !== 'de') {
                setMobileNumber(urlMsisdn);
                console.log('Pre-filled mobile number from URL:', urlMsisdn);
            }

            // Check if session is already subscribed for non-PT flow (only if session exists)
            if (session && !hasCheckedSession.current) {
                hasCheckedSession.current = true;
                checkSessionAndRedirect(session);
            }
        }
    }, []); // Ensure this runs only once on mount

    // Update password requirements only for non-PT, non-Arabic, and non-German flow
    useEffect(() => {
        if (!isPortugueseFlow && !isArabicFlow && !isGermanFlow) {
            setPasswordRequirements({
                minLength: password.length >= 8,
                hasUpperCase: /[A-Z]/.test(password),
                hasLowerCase: /[a-z]/.test(password),
                hasNumber: /[0-9]/.test(password),
                hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
                matching: password === confirmPassword && password !== ''
            });
        }
    }, [password, confirmPassword, isPortugueseFlow, isArabicFlow]);

    // --- Event Handlers ---

    // Handles manual signup form submission (non-PT and non-Arabic flow)
    const handleManualSignup = async (e) => {
        e.preventDefault();
        
        // Double-check URL parameters to prevent manual submission for Arabic/PT flows
        const params = new URLSearchParams(window.location.search);
        const urlLang = params.get('lang');
        if (urlLang === 'ae') {
            console.error('Manual signup attempted for Arabic flow (lang=ae). This should not happen.');
            setAlertMessage('Automatic signup is in progress. Please wait...');
            setAlertColor('blue');
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
            return;
        }
        
        if (isPortugueseFlow || isArabicFlow || isGermanFlow) {
            console.error('Manual signup attempted while PT/Arabic/German flow is active. This should not happen.');
            return;
        }

        const allRequirementsMet = Object.values(passwordRequirements).every(req => req);
        if (!allRequirementsMet) {
            setAlertMessage(t.passwordRequirementsNotMet || 'Please meet all password requirements');
            setAlertColor('red');
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
            return;
        }

        // Get sessionId from URL if not in state
        const sessionFromUrl = params.get('s');
        const finalSessionId = sessionId || sessionFromUrl || '';
        
        if (!finalSessionId) {
            setAlertMessage('Session ID is required. Please use the signup link provided.');
            setAlertColor('red');
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 5000);
            return;
        }

        const payload = { username, password, email, mobileNumber, sessionId: finalSessionId };
        console.log('Signup payload:', { ...payload, password: '[REDACTED]' });
        try {
            const response = await fetch('/api/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (response.ok) {
                // Store sessionId as accountSessionId in localStorage
                if (finalSessionId) {
                    localStorage.setItem('accountSessionId', finalSessionId);
                    localStorage.setItem('sessionId', finalSessionId);
                    console.log('SessionId stored in localStorage:', finalSessionId);
                }
                
                // Handle both new signup and "already exists" cases
                if (data.alreadyExists) {
                    console.log('User already exists, proceeding with login using returned user data.');
                    if (data.userId) {
                        localStorage.setItem('userId', String(data.userId));
                    }
                    if (data.username) {
                        localStorage.setItem('username', String(data.username));
                    } else {
                        localStorage.setItem('username', String(username));
                    }
                } else {
                    // New user created
                    if (data.userId) {
                        localStorage.setItem('userId', String(data.userId));
                    }
                    localStorage.setItem('username', String(username));
                }
                
                try {
                    Cookies.set('isLoggedIn', 'true', { expires: 1 }); // 1 day
                } catch (cookieError) {
                    console.error('Error setting cookie:', cookieError);
                    Cookies.set('isLoggedIn', 'true');
                }
                if (onLogin) { onLogin(); }
                navigate('/');
            } else {
                // Check if it's a duplicate entry error that we should handle gracefully
                if (data.error?.includes('already exists') || data.details?.includes('Duplicate entry')) {
                    console.log('User already exists, attempting to login instead...');
                    // Try to get user data from check-session
                    if (sessionId) {
                        try {
                            const checkSessionResponse = await fetch(`/api/login/check-session?sessionId=${sessionId}`);
                            const checkSessionData = await checkSessionResponse.json();
                            if (checkSessionResponse.ok && checkSessionData.isValid && checkSessionData.userId) {
                                localStorage.setItem('userId', String(checkSessionData.userId));
                                if (checkSessionData.username) {
                                    localStorage.setItem('username', String(checkSessionData.username));
                                } else {
                                    localStorage.setItem('username', String(username));
                                }
                                try {
                                    Cookies.set('isLoggedIn', 'true', { expires: 1 });
                                } catch (cookieError) {
                                    Cookies.set('isLoggedIn', 'true');
                                }
                                if (onLogin) { onLogin(); }
                                navigate('/');
                                return;
                            }
                        } catch (checkError) {
                            console.error('Error checking session:', checkError);
                        }
                    }
                }
                
                setAlertMessage(data.error || t.signupError);
                setAlertColor('red');
                setShowAlert(true);
                setTimeout(() => setShowAlert(false), 3000);
            }
        } catch (error) {
            console.error('Manual Signup error:', error);
            setAlertMessage(t.signupError || 'Error during signup');
            setAlertColor('red');
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 3000);
        }
    };

    // Helper to clear URL parameters after successful signup navigation
    const clearUrlParams = () => {
        // Replace current URL without parameters to prevent re-triggering signup logic
        window.history.replaceState({}, document.title, window.location.pathname);
    };

    // Handler for the Home button on the PT confirmation screen
    const handlePtHomeClick = () => {
        if (onLogin) {
            onLogin(); // Update App state to logged in
        }
        clearUrlParams(); // Clear s= and lang= parameters
        navigate('/'); // Navigate to home
    };

    // Handler for the Profile link on the PT confirmation screen
    const handlePtProfileClick = () => {
        if (onLogin) {
            onLogin(); // Update App state to logged in
        }
        clearUrlParams(); // Clear s= and lang= parameters
        navigate('/profile'); // Navigate to profile
    };

    // Handler for the Home button on the Arabic confirmation screen
    const handleArabicHomeClick = () => {
        if (onLogin) {
            onLogin(); // Update App state to logged in
        }
        clearUrlParams(); // Clear s= and lang= parameters
        navigate('/'); // Navigate to home
    };

    // Handler for the Profile link on the Arabic confirmation screen
    const handleArabicProfileClick = () => {
        if (onLogin) {
            onLogin(); // Update App state to logged in
        }
        clearUrlParams(); // Clear s= and lang= parameters
        navigate('/profile'); // Navigate to profile
    };

    // Handler for the Home button on the German confirmation screen
    const handleGermanHomeClick = () => {
        if (onLogin) {
            onLogin(); // Update App state to logged in
        }
        clearUrlParams(); // Clear s= and lang= parameters
        navigate('/'); // Navigate to home
    };

    // Handler for the Profile link on the German confirmation screen
    const handleGermanProfileClick = () => {
        if (onLogin) {
            onLogin(); // Update App state to logged in
        }
        clearUrlParams(); // Clear s= and lang= parameters
        navigate('/profile'); // Navigate to profile
    };

    // --- Render Logic ---

    const RequirementIcon = ({ met }) => (
        met ? <CheckCircleIcon className="h-5 w-5 text-green-500" /> : <XCircleIcon className="h-5 w-5 text-gray-300" />
    );

    // CRITICAL: Check URL first - NEVER show form if lang=ae (or variants)
    const params = new URLSearchParams(window.location.search);
    const urlLang = params.get('lang');
    const isUAEUrl = urlLang === 'ae' || urlLang === 'ae-ar' || urlLang === 'ae-en' || 
                     urlLang === 'uae-ar' || urlLang === 'uae-en' || urlLang === 'uae-arabic' || urlLang === 'uae-english';
    if (isUAEUrl) {
        // If Arabic flow is not yet initialized or status is idle, show loading
        if (!isArabicFlow || arabicSignupStatus === 'idle') {
            return (
                <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                    <Spinner className="h-12 w-12" />
                    <Typography className="mt-4" dir="rtl">جارٍ التحميل...</Typography>
                </div>
            );
        }
        // If we reach here and Arabic flow is active, one of the screens below will handle it
        // But if somehow we get here, show loading as fallback
    }

    // Loading/Validating state for PT flow
    if (isPortugueseFlow && (ptSignupStatus === 'processing_signup' || ptSignupStatus === 'validating')) {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <Spinner className="h-12 w-12" />
                <Typography className="mt-4">
                    {ptSignupStatus === 'validating' ? 'A validar sessão...' : 'A processar registo...'}
                </Typography>
            </div>
        );
    }

    // Loading/Validating state for Arabic flow
    if (isArabicFlow && (arabicSignupStatus === 'processing_signup' || arabicSignupStatus === 'validating')) {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <Spinner className="h-12 w-12" />
                <Typography className="mt-4" dir="rtl">
                    {arabicSignupStatus === 'validating' ? 'جارٍ التحقق من الجلسة...' : 'جارٍ معالجة التسجيل...'}
                </Typography>
            </div>
        );
    }

    // Loading/Validating state for German flow
    if (isGermanFlow && (germanSignupStatus === 'processing_signup' || germanSignupStatus === 'validating')) {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <Spinner className="h-12 w-12" />
                <Typography className="mt-4">
                    {germanSignupStatus === 'validating' ? 'Sitzung wird überprüft...' : 'Registrierung wird verarbeitet...'}
                </Typography>
            </div>
        );
    }

    // PT Flow Confirmation Screen (only if signup was successful or for fake MSISDN)
    if (isPortugueseFlow && ptSignupStatus === 'success') {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100 text-gray-800 ${mcDePageClass(language)}`}>
                <div className="w-full max-w-sm bg-white p-6 rounded-lg shadow-md text-center">
                    <img src={logo} alt="Service Logo" className="h-16 mx-auto mb-4" />
                    <Typography variant="h5" className="mb-3 font-semibold">Subscrição efetuada</Typography>
                    <Typography variant="small" className="mb-3">
                        Obrigado por utilizar os nossos serviços. Esta subscrição tem um custo de 1.99€/semana.
                        Para cancelar, carregue <NavLink to="/cancelar-subscricao" className="underline text-blue-600 hover:text-blue-800">aqui</NavLink>.
                    </Typography>
                    <div className="text-left my-4 p-3 bg-gray-50 rounded border border-gray-200">
                        <Typography variant="small" className="font-semibold mb-1">Os seus dados de registo:</Typography>
                        <Typography variant="small">Utilizador: {msisdn}</Typography>
                        <Typography variant="small">Password: {generatedPassword}</Typography>
                    </div>
                    <Typography variant="small" className="mb-4">
                        Sendo a primeira vez que acede sugerimos mudar a sua password em{" "}
                        <span
                            className="underline text-blue-600 hover:text-blue-800 cursor-pointer"
                            onClick={handlePtProfileClick}
                        >
                            Gestão de conta
                        </span>.
                    </Typography>
                    {/* Updated Home button onClick */}
                    <Button color="blue" className="w-full mb-4" onClick={handlePtHomeClick}>Home</Button>
                    <div className="text-xs text-gray-600">
                        <Typography variant="small" className="mb-1">Apoio a cliente: 707450201</Typography>
                        <Typography variant="small" className="mb-2">
                            <a href="mailto:help@clickzillas.com" className="underline hover:text-blue-800">help@clickzillas.com</a>
                        </Typography>
                        <Typography variant="small">
                            <a href="https://souschaif.com/pt/termos-e-condicoes/" className="underline hover:text-blue-800">Termos e Condições</a>
                        </Typography>
                    </div>
                </div>
                {/* Alert for any messages during PT flow */}
                {showAlert && (
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                        <Alert
                            open={showAlert}
                            color={alertColor}
                            className="flex justify-between items-center"
                            animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                            action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }

    // PT Flow Error Screen (Show error/message instead of form)
    if (isPortugueseFlow && ptSignupStatus === 'error') {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <img src={logo} alt={brandName} className="h-24 mb-4" />
                <Typography variant="h5" color="red" className="mb-4">Erro</Typography>
                <Typography className="mb-4 text-center">{alertMessage}</Typography>
                <Button onClick={() => navigate('/login')}>Voltar ao Login</Button>
                {/* Keep alert rendering consistent */}
                {showAlert && (
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                        <Alert
                            open={showAlert}
                            color={alertColor} // Should be 'red' in error state
                            className="flex justify-between items-center"
                            animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                            action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }

    // Arabic Flow Confirmation Screen (only if signup was successful or for fake MSISDN)
    if (isArabicFlow && arabicSignupStatus === 'success') {
        // Check if it's UAE based on URL parameter, not language selector
        const params = new URLSearchParams(window.location.search);
        const urlLang = params.get('lang');
        // Support all UAE URL variants: ae, ae-ar, ae-en, uae-ar, uae-en, etc.
        const isUAE = urlLang === 'ae' || urlLang === 'ae-ar' || urlLang === 'ae-en' || 
                     urlLang === 'uae-ar' || urlLang === 'uae-en' || urlLang === 'uae-arabic' || urlLang === 'uae-english';
        
        // Determine if Arabic or English for UAE
        // Priority: 1) URL parameter lang=ae-ar/ae-en, 2) Language context, 3) Default to English
        const urlLangFull = params.get('lang'); // Could be 'ae', 'ae-ar', 'ae-en', 'uae-ar', 'uae-en', etc.
        let isUAEArabic = false;
        let isUAEEnglish = false;
        
        if (isUAE) {
            // Check URL parameter first for explicit language specification
            if (urlLangFull === 'ae-ar' || urlLangFull === 'uae-ar' || urlLangFull === 'uae-arabic') {
                isUAEArabic = true;
                isUAEEnglish = false;
            } else if (urlLangFull === 'ae-en' || urlLangFull === 'uae-en' || urlLangFull === 'uae-english') {
                isUAEArabic = false;
                isUAEEnglish = true;
            } else if (urlLangFull === 'ae') {
                // If just 'ae', check language context to determine Arabic or English
                isUAEArabic = language === 'uae-ar' || language === 'uae-arabic' || language === 'arabic';
                isUAEEnglish = !isUAEArabic; // Default to English if not Arabic
            } else {
                // Fallback: check language context
                isUAEArabic = language === 'uae-ar' || language === 'uae-arabic' || language === 'arabic';
                isUAEEnglish = !isUAEArabic; // Default to English if not Arabic
            }
        }

        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100 text-gray-800 ${isUAEArabic ? 'dir-rtl' : ''} ${mcDePageClass(language)}`} dir={isUAEArabic ? 'rtl' : 'ltr'}>
                <div className="w-full max-w-sm bg-white p-6 rounded-lg shadow-md text-center">
                    <img src={logo} alt="Service Logo" className="h-16 mx-auto mb-4" />
                    {isUAE ? (
                        // UAE specific content
                        <>
                            <Typography variant="h5" className="mb-3 font-semibold">
                                {isUAEArabic ? 'تم الاشتراك بنجاح' : 'Subscription Successful'}
                            </Typography>
                            <div className={`my-4 p-3 bg-gray-50 rounded border border-gray-200 ${isUAEArabic ? 'text-right' : 'text-left'}`}>
                                <Typography variant="small" className="font-semibold mb-1">
                                    {isUAEArabic ? 'بيانات التسجيل الخاصة بك:' : 'Your registration details:'}
                                </Typography>
                                <Typography variant="small">
                                    {isUAEArabic ? 'المستخدم: ' : 'Username: '}{msisdn}
                                </Typography>
                                <Typography variant="small">
                                    {isUAEArabic ? 'كلمة المرور: ' : 'Password: '}{generatedPassword}
                                </Typography>
                            </div>
                            <Typography variant="small" className="mb-4">
                                {isUAEArabic ? (
                                    <>
                                        بما أن هذه المرة الأولى التي تصل فيها، ننصح بتغيير كلمة المرور في{" "}
                                        <span
                                            className="underline text-blue-600 hover:text-blue-800 cursor-pointer"
                                            onClick={handleArabicProfileClick}
                                        >
                                            إدارة الحساب
                                        </span>.
                                    </>
                                ) : (
                                    <>
                                        Since this is your first time accessing, we suggest changing your password in{" "}
                                        <span
                                            className="underline text-blue-600 hover:text-blue-800 cursor-pointer"
                                            onClick={handleArabicProfileClick}
                                        >
                                            Account Management
                                        </span>.
                                    </>
                                )}
                            </Typography>
                            <Button color="blue" className="w-full mb-4" onClick={handleArabicHomeClick}>
                                {isUAEArabic ? 'الرئيسية' : 'Home'}
                            </Button>
                            <div className="text-xs text-gray-600">
                                <Typography variant="small" className="mb-2">
                                    {isUAEArabic ? 'للحصول على الدعم، يرجى الاتصال: ' : 'To get support, please contact: '}
                                    <a href="mailto:help@mobixas.com" className="underline hover:text-blue-800">help@mobixas.com</a>
                                </Typography>
                            </div>
                        </>
                    ) : (
                        // Generic Arabic content (for other Arabic countries)
                        <>
                            <Typography variant="h5" className="mb-3 font-semibold">تم الاشتراك بنجاح</Typography>
                            <Typography variant="small" className="mb-3">
                                شكراً لاستخدام خدماتنا. تكلفة هذا الاشتراك 1.99€/أسبوع.
                            </Typography>
                            <div className="text-right my-4 p-3 bg-gray-50 rounded border border-gray-200">
                                <Typography variant="small" className="font-semibold mb-1">بيانات التسجيل الخاصة بك:</Typography>
                                <Typography variant="small">المستخدم: {msisdn}</Typography>
                                <Typography variant="small">كلمة المرور: {generatedPassword}</Typography>
                            </div>
                            <Typography variant="small" className="mb-4">
                                بما أن هذه المرة الأولى التي تصل فيها، ننصح بتغيير كلمة المرور في{" "}
                                <span
                                    className="underline text-blue-600 hover:text-blue-800 cursor-pointer"
                                    onClick={handleArabicProfileClick}
                                >
                                    إدارة الحساب
                                </span>.
                            </Typography>
                            <Button color="blue" className="w-full mb-4" onClick={handleArabicHomeClick}>الرئيسية</Button>
                            <div className="text-xs text-gray-600">
                                <Typography variant="small" className="mb-2">
                                    <a href="mailto:help@mobixas.com" className="underline hover:text-blue-800">help@mobixas.com</a>
                                </Typography>
                                <Typography variant="small">
                                    <a href="https://souschaif.com/pt/termos-e-condicoes/" className="underline hover:text-blue-800">الشروط والأحكام</a>
                                </Typography>
                            </div>
                        </>
                    )}
                </div>
                {/* Alert for any messages during Arabic flow */}
                {showAlert && (
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                        <Alert
                            open={showAlert}
                            color={alertColor}
                            className="flex justify-between items-center"
                            animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                            action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }

    // Arabic Flow Error Screen (Show error/message instead of form)
    if (isArabicFlow && arabicSignupStatus === 'error') {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`} dir="rtl">
                <img src={logo} alt={brandName} className="h-24 mb-4" />
                <Typography variant="h5" color="red" className="mb-4">خطأ</Typography>
                <Typography className="mb-4 text-center">{alertMessage}</Typography>
                <Button onClick={() => navigate('/login')}>العودة إلى تسجيل الدخول</Button>
                {/* Keep alert rendering consistent */}
                {showAlert && (
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                        <Alert
                            open={showAlert}
                            color={alertColor} // Should be 'red' in error state
                            className="flex justify-between items-center"
                            animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                            action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }

    // German Flow Confirmation Screen (only if signup was successful)
    if (isGermanFlow && germanSignupStatus === 'success') {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 bg-gray-100 text-gray-800 ${mcDePageClass(language)}`}>
                <div className="w-full max-w-sm bg-white p-6 rounded-lg shadow-md text-center">
                    <img src={logo} alt="Service Logo" className="h-16 mx-auto mb-4" />
                    <Typography variant="h5" className="mb-3 font-semibold">Abonnement erfolgreich</Typography>
                    <Typography variant="small" className="mb-3">
                        Vielen Dank für die Nutzung unserer Dienste.
                    </Typography>
                    <div className="text-left my-4 p-3 bg-gray-50 rounded border border-gray-200">
                        <Typography variant="small" className="font-semibold mb-1">Ihre Registrierungsdaten:</Typography>
                        <Typography variant="small">Benutzer: {msisdn}</Typography>
                        <Typography variant="small">Passwort: {generatedPassword}</Typography>
                    </div>
                    <Typography variant="small" className="mb-4">
                        Da dies Ihr erstes Mal ist, empfehlen wir, Ihr Passwort in{" "}
                        <span
                            className="underline text-blue-600 hover:text-blue-800 cursor-pointer"
                            onClick={handleGermanProfileClick}
                        >
                            Kontoverwaltung
                        </span> zu ändern.
                    </Typography>
                    <Button color="blue" className="w-full mb-4" onClick={handleGermanHomeClick}>Startseite</Button>
                    <div className="text-xs text-gray-600">
                        <Typography variant="small" className="mb-2">
                            <a href="mailto:help@amintabv.com" className="underline hover:text-blue-800">help@amintabv.com</a>
                        </Typography>
                        <Typography variant="small">
                            <a href={isMaisterchef ? "https://maisterchef.com/dede/allgemeine-geschaftsbedingungen-3/" : "https://souschaif.com/dede/allgemeine-geschaftsbedingungen-3/"} className="underline hover:text-blue-800">Allgemeine Geschäftsbedingungen</a>
                        </Typography>
                    </div>
                </div>
                {/* Alert for any messages during German flow */}
                {showAlert && (
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                        <Alert
                            open={showAlert}
                            color={alertColor}
                            className="flex justify-between items-center"
                            animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                            action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }

    // German Flow Error Screen (Show error/message instead of form)
    if (isGermanFlow && germanSignupStatus === 'error') {
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <img src={logo} alt={brandName} className="h-24 mb-4" />
                <Typography variant="h5" color="red" className="mb-4">Fehler</Typography>
                <Typography className="mb-4 text-center">{alertMessage}</Typography>
                <Button onClick={() => navigate('/login')}>Zurück zum Login</Button>
                {/* Keep alert rendering consistent */}
                {showAlert && (
                    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                        <Alert
                            open={showAlert}
                            color={alertColor} // Should be 'red' in error state
                            className="flex justify-between items-center"
                            animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                            action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                        >
                            {alertMessage}
                        </Alert>
                    </div>
                )}
            </div>
        );
    }


    // Additional safeguard: Don't show form if Arabic flow is active
    if (isArabicFlow && arabicSignupStatus !== 'success' && arabicSignupStatus !== 'error') {
        // Show loading if Arabic flow is active but not in success/error state
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <Spinner className="h-12 w-12" />
                <Typography className="mt-4" dir="rtl">جارٍ معالجة التسجيل...</Typography>
            </div>
        );
    }

    // Additional safeguard: Don't show form if German flow is active
    if (isGermanFlow && germanSignupStatus !== 'success' && germanSignupStatus !== 'error') {
        // Show loading if German flow is active but not in success/error state
        return (
            <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
                <Spinner className="h-12 w-12" />
                <Typography className="mt-4">Registrierung wird verarbeitet...</Typography>
            </div>
        );
    }

    // Render Normal Signup Form (only if NOT Arabic flow and NOT PT flow)
    return (
        <div className={`flex flex-col items-center justify-center min-h-screen p-4 ${mcDePageClass(language)}`}>
            <img src={logo} alt={brandName} className="h-24 mb-4" />
            <Typography variant="h4" className="mb-2">{t.signup}</Typography>
            <Typography variant="paragraph" className="mb-4 text-center">{t.signupIntro}</Typography>

            {/* Already subscribed link - moved to top */}
            <div className="mb-4 text-center">
                <Typography variant="small" className="text-gray-600">
                    {t.alreadySubscribed || 'Already subscribed?'}
                    <NavLink to="/login" className="ml-1 text-blue-600 hover:text-blue-800 underline">
                        {t.clickHere || 'Click here'}
                    </NavLink>
                </Typography>
            </div>

            <form onSubmit={handleManualSignup} className="w-full max-w-sm flex flex-col gap-4">
                {/* Username Field */}
                <Input label={t.username} value={username} onChange={(e) => setUsername(e.target.value)} className="bg-white" required />
                {/* Password Field */}
                <Input type="password" label={t.password} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-white" required />
                {/* Confirm Password */}
                <Input type="password" label={t.confirmPassword} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="bg-white" required />
                {/* Password Requirements Box */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <Typography variant="small" className="font-medium mb-2">{t.passwordRequirements}:</Typography>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2"><RequirementIcon met={passwordRequirements.minLength} /><span className={passwordRequirements.minLength ? "text-green-500" : "text-gray-600"}>{t.minLength}</span></div>
                        <div className="flex items-center gap-2"><RequirementIcon met={passwordRequirements.hasUpperCase} /><span className={passwordRequirements.hasUpperCase ? "text-green-500" : "text-gray-600"}>{t.requireUppercase}</span></div>
                        <div className="flex items-center gap-2"><RequirementIcon met={passwordRequirements.hasLowerCase} /><span className={passwordRequirements.hasLowerCase ? "text-green-500" : "text-gray-600"}>{t.requireLowercase}</span></div>
                        <div className="flex items-center gap-2"><RequirementIcon met={passwordRequirements.hasNumber} /><span className={passwordRequirements.hasNumber ? "text-green-500" : "text-gray-600"}>{t.requireNumber}</span></div>
                        <div className="flex items-center gap-2"><RequirementIcon met={passwordRequirements.hasSpecial} /><span className={passwordRequirements.hasSpecial ? "text-green-500" : "text-gray-600"}>{t.requireSpecial}</span></div>
                        <div className="flex items-center gap-2"><RequirementIcon met={passwordRequirements.matching} /><span className={passwordRequirements.matching ? "text-green-500" : "text-gray-600"}>{t.passwordsMatch}</span></div>
                    </div>
                </div>
                {/* Email Field */}
                <Input type="email" label={t.email} value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white" required />
                {/* Mobile Number Field */}
                <Input label={t.mobileNumber} value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} className="bg-white" required />
                {/* Submit Button */}
                <Button type="submit" className="w-full" color="green" disabled={!Object.values(passwordRequirements).every(req => req)}>
                    {t.signupButton}
                </Button>
            </form>

            {/* Alert for non-PT, non-Arabic, and non-German flow */}
            {showAlert && !isPortugueseFlow && !isArabicFlow && !isGermanFlow && ( // Only show this alert if not PT, Arabic, or German flow
                <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 max-w-md w-full z-50 px-4">
                    <Alert
                        open={showAlert}
                        color={alertColor}
                        className="flex justify-between items-center"
                        animate={{ mount: { y: 0 }, unmount: { y: 100 } }}
                        action={<IconButton variant="text" color="white" size="sm" onClick={() => setShowAlert(false)}> <XMarkIcon className="h-6 w-6" /> </IconButton>}
                    >
                        {alertMessage}
                    </Alert>
                </div>
            )}
        </div>
    );
};

export default Signup;
